import { useState, useEffect, useRef } from "react";
import { useToggleState } from "../../../../../packages/hooks";
import { useInputHeightContext, useDropzoneContext } from "../../../contexts";
import { getAttachedFile } from "../../../providers";
import {
  getListHeight,
  getAttachedFilesCount,
  extractFilesFromScreenshot,
} from "../helpers";
import { useControlContext } from "../../../../control";
import { MAX_LIMIT } from "./constants";
import type { ChangeEvent } from "react";
import type { AttachedFiles } from "../../../types";

export const useFileController = () => {
  const { handleChangeFileListHeight } = useInputHeightContext();
  const { exceedFileLimit, resetFileLimitExceeded } = useDropzoneContext();
  const [attachedFiles, setAttachedFiles] = useState<AttachedFiles>([]);
  const { feature } = useControlContext();
  const {
    toggle: isFileLimitExceeded,
    toggleOn: showLimitExceededError,
    toggleOff: hideLimitExceededError,
  } = useToggleState();

  const {
    toggle: isFileAttached,
    toggleOn: setFileAttachedState,
    toggleOff: removeFileAttachedState,
  } = useToggleState();

  const fileInputRef = useRef<HTMLInputElement>({} as HTMLInputElement);
  const attachedFilesLength = attachedFiles.length;
  const hasFiles = !!attachedFilesLength;
  const hasInvalidFiles = attachedFiles.some(({ error }) => error);
  const isFileLimitExhausted = attachedFilesLength >= MAX_LIMIT;
  const attachedFilesCount = getAttachedFilesCount(attachedFilesLength);
  const attachedFileNames = useRef<Set<string>>(new Set<string>());

  const handleAttachFiles = (files: FileList) => {
    removeFileAttachedState();
    hideLimitExceededError();

    setAttachedFiles((prevFiles) => {
      const filesArray = Array.from(files);
      const isMoreThanLimit = prevFiles.length + filesArray.length > MAX_LIMIT;

      if (isMoreThanLimit) {
        showLimitExceededError();
      }

      if (prevFiles.length >= MAX_LIMIT) {
        return prevFiles;
      }

      const filteredUniqueFiles = filesArray
        .filter(({ name }) => !attachedFileNames.current.has(name))
        .slice(0, MAX_LIMIT - prevFiles.length);

      filteredUniqueFiles.forEach(({ name }) =>
        attachedFileNames.current.add(name)
      );

      const newAttachedFiles = filteredUniqueFiles.map(getAttachedFile);

      return [...prevFiles, ...newAttachedFiles];
    });
    setTimeout(setFileAttachedState, 100);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target?.files?.length) {
      return;
    }

    handleAttachFiles(e.target.files);
  };

  const handleDropFile = (event: DragEvent) => {
    event.preventDefault();

    if (feature?.chatFileSending && event?.dataTransfer?.files.length) {
      handleAttachFiles(event.dataTransfer.files);
      event.dataTransfer.clearData();
    }
  };

  const handlePasteScreenshot = (event: ClipboardEvent) => {
    const files = extractFilesFromScreenshot(event);

    if (files) {
      handleAttachFiles(files);
    }
  };

  const handleRemoveFile = (removeId: string) => () => {
    removeFileAttachedState();
    hideLimitExceededError();
    // input хранит путь последнего загруженного файла в value
    // если удалить файл который числится в value, повторно его подгрузить не удастся
    // поэтому для корректной работы нужно что бы в value было пусто
    // а все файлы лежат в files = FileList
    fileInputRef.current.value = "";

    const fileToRemove = attachedFiles.find(({ id }) => removeId === id);

    if (fileToRemove) {
      attachedFileNames.current.delete(fileToRemove?.file.name);
    }

    setAttachedFiles((prevFiles) =>
      prevFiles.filter(({ id }) => removeId !== id)
    );
  };

  const handleCleanFiles = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setAttachedFiles([]);
    attachedFileNames.current.clear();
  };

  useEffect(() => {
    if (isFileLimitExhausted) {
      exceedFileLimit();
    } else {
      resetFileLimitExceeded();
    }
  }, [isFileLimitExhausted]);

  useEffect(() => {
    const chatDropzone = document.getElementById("chat-dropzone");

    chatDropzone?.addEventListener("drop", handleDropFile);

    document.addEventListener("paste", handlePasteScreenshot);

    return () => {
      chatDropzone?.removeEventListener("drop", handleDropFile);
      document.removeEventListener("paste", handlePasteScreenshot);
    };
  }, []);

  useEffect(() => {
    const fileListHeight = getListHeight(attachedFilesLength, hasInvalidFiles);

    handleChangeFileListHeight(fileListHeight);
  }, [attachedFilesLength, hasInvalidFiles]);

  return {
    files: attachedFiles,
    attachedFilesCount,
    hasFiles,
    hasInvalidFiles,

    isFileAttached,
    isFileLimitExhausted,
    isFileLimitExceeded,

    fileInputRef,

    handleFileInputChange,
    handleRemoveFile,
    handleCleanFiles,
  };
};
